import React, { ReactNode, useEffect, useMemo } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Modal as BootstrapModal, Tab } from 'react-bootstrap'

import {
  selectContactChange,
  selectIsLoadingContact,
  selectIsLoadingContactChange,
  selectIsLoadingRerun,
  selectIsLoadingSave,
  selectSelectedKeys,
  setExistingExternalContact,
  setIsLoadingContact,
  setSelectedKeys,
  setSelectedZocksContact,
  setShouldCreateContact,
  setShouldLinkContact,
  setShowCreateContact,
  setShowLinkContact,
} from '../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { Button } from '../../components/Button/Button'
import { Tabs } from '../../components/Tabs/Tabs'

import { UpdateExternalContactForm, UpdateExternalContactFormFooterComponent } from './UpdateExternalContactForm'
import { filterIgnoreFieldsFromKeys, getAllKeysInData } from './utils'
import { ExternalContactUpdateProps, useRerunContactUpdate, useSaveContactUpdate } from './hooks'
import { getIntegrationDisplayName, getIntegrationCategoryDisplayNameByServiceId, IntegrationServiceId } from '../../common/integrations'
import { useIntegrationExportConfirmation } from '../../components/ConfirmationDialog/useIntegrationExportConfirmation'
import { User } from '../../models/User'
import { Modal } from '../../components/Modal/Modal'
import { UpdateExternalContactFormV2 } from './UpdateExternalContactFormV2'
import { ContactUpdateFormValues } from './models'

interface UpdateExternalContactModalProps {
  user: User
  serviceId: string
  show: boolean
  onCancel: () => void
  BodyComponent: ReactNode
  FooterComponent: ReactNode
}

export const UpdateExternalContactModal: React.FC<UpdateExternalContactModalProps> = ({ serviceId, user, show, onCancel, BodyComponent, FooterComponent }) => {
  const dispatch = useAppDispatch()

  const contactChange = useAppSelector(selectContactChange)

  const form = useForm<ContactUpdateFormValues>()

  useEffect(() => {
    if (!contactChange) {
      return
    }
    form.setValue('contactValues', contactChange.contactValues)
    const keys = getAllKeysInData(contactChange.contactValues, 'contactValues')
    dispatch(setSelectedKeys(keys))
  }, [contactChange, dispatch, form])

  useEffect(() => {
    const integration = user.integrations?.find((e) => e.serviceId === serviceId)
    if (integration) {
      return
    }

    dispatch(setSelectedZocksContact(user))
    dispatch(setIsLoadingContact(false))
    dispatch(setExistingExternalContact(false))

    if (user.type === 'anonymous') {
      dispatch(setShowCreateContact(true))
      dispatch(setShouldCreateContact(true))
      return
    }

    dispatch(setShowLinkContact(true))
    dispatch(setShouldLinkContact(true))
  }, [dispatch, serviceId, user])

  const integrationDisplayName = getIntegrationDisplayName(serviceId as IntegrationServiceId)

  return (
    <FormProvider {...form}>
      <Modal
        className='modal-lg'
        show={show}
        onHide={onCancel}
        header={
          <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-start'>
            Save Contact To {integrationDisplayName}
          </BootstrapModal.Title>
        }
        body={BodyComponent}
        footer={FooterComponent}
        footerClassName='d-flex justify-content-between align-items-center'
        centered={true}
        bodyClassName='mt-3'
        headerBodySeparator={true}
      />
    </FormProvider>
  )
}

export interface ExternalContactUpdateViewProps {
  serviceId: string
  contactId: string
  activeTabKey?: string
  setActiveTabKey?: React.Dispatch<React.SetStateAction<string>>
  Tabs?: ReactNode
  onKeySelected?: (selectedKey: string, isUnselect: boolean) => void
}

export const DefaultExternalContactUpdateView: React.FC<ExternalContactUpdateViewProps> = ({
  setActiveTabKey,
  activeTabKey,
  serviceId,
  Tabs: _Tabs,
  onKeySelected,
}) => {
  const isLoadingContactChange = useAppSelector(selectIsLoadingContactChange)
  const contactChange = useAppSelector(selectContactChange)

  if (!activeTabKey || !setActiveTabKey) {
    return null
  }

  const changeTab = (key: string | null) => {
    if (key) {
      setActiveTabKey(key)
    }
  }

  if (isLoadingContactChange) {
    return <div className='skeleton contact-information-skeleton d-flex align-items-center justify-content-center w-100'>Loading…</div>
  }

  if (!contactChange) {
    return <UpToDate serviceId={serviceId} />
  }

  return (
    <Tabs activeKey={activeTabKey} id='contact-update-tabs' className='d-flex mb-3' onSelect={changeTab} mountOnEnter>
      <Tab eventKey='contactFieldsUpdate' title='Update Contact Fields'>
        <UpdateExternalContactForm
          serviceId={serviceId}
          contactSchema={contactChange.contactSchema}
          previousContactValues={contactChange.previousContactValues}
          FooterComponent={UpdateExternalContactFormFooterComponent}
          onKeySelected={onKeySelected}
        />
      </Tab>
      {_Tabs}
    </Tabs>
  )
}

export const DefaultExternalContactUpdateViewV2: React.FC<ExternalContactUpdateViewProps> = ({ serviceId }) => {
  const isLoadingContactChange = useAppSelector(selectIsLoadingContactChange)
  const contactChange = useAppSelector(selectContactChange)

  if (isLoadingContactChange) {
    return <div className='skeleton contact-information-skeleton d-flex align-items-center justify-content-center w-100'>Loading…</div>
  }

  if (!contactChange) {
    return <UpToDate serviceId={serviceId} />
  }

  const { contactSchema, contactValues, previousContactValues } = contactChange

  return (
    <UpdateExternalContactFormV2
      serviceId={serviceId}
      rootFieldId='contactValues'
      contactSchema={contactSchema}
      contactValues={contactValues}
      previousContactValues={previousContactValues}
      requiredSteps={{}}
      metadata={{}}
    />
  )
}

interface UpdateExternalContactFooterProps {
  serviceId: string
  user: User
  updateProps: ExternalContactUpdateProps
  onCancel: () => void
  onSave?: () => void
  onRerun?: () => void
}

export const UpdateExternalContactModalFooter: React.FC<UpdateExternalContactFooterProps> = ({ serviceId, user, updateProps, onCancel, onSave, onRerun }) => {
  const { id: contactId } = user

  const isLoadingContact = useAppSelector(selectIsLoadingContact)
  const contactChange = useAppSelector(selectContactChange)
  const isLoadingContactChange = useAppSelector(selectIsLoadingContactChange)
  const selectedKeys = useAppSelector(selectSelectedKeys)
  const isLoadingSave = useAppSelector(selectIsLoadingSave)
  const isLoadingRerun = useAppSelector(selectIsLoadingRerun)

  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useIntegrationExportConfirmation()

  const { handleSubmit, formState } = useFormContext<ContactUpdateFormValues>()

  const isSaveDisabled = useMemo(
    () =>
      isLoadingContact ||
      isLoadingContactChange ||
      accountIsLoading ||
      !contactChange ||
      !filterIgnoreFieldsFromKeys(serviceId, selectedKeys).length ||
      !!Object.keys(formState.errors).length,
    [contactChange, formState.errors, isLoadingContact, isLoadingContactChange, selectedKeys, serviceId, accountIsLoading],
  )

  const { rerunContactUpdate } = useRerunContactUpdate(serviceId, user, updateProps, onRerun)
  const { saveContactUpdate } = useSaveContactUpdate({ serviceId, contactId, user, onSave })

  const handleSave = async (data: ContactUpdateFormValues) => {
    await runMutationWithConfirmation(async () => {
      await saveContactUpdate(data)
    })
  }

  const handleRerun = async () => {
    await rerunContactUpdate()
  }

  return (
    <div className='d-flex flex-column gap-2 w-100'>
      <div className='d-flex align-items-center justify-content-between'>
        <Button className='mt-2 me-auto' variant='primary' onClick={handleRerun} disabled={isLoadingContact || isLoadingContactChange} loading={isLoadingRerun}>
          Rerun
        </Button>
        <div className='d-flex align-items-center gap-2'>
          <Button className='mt-2 me-auto' variant='secondary' onClick={onCancel}>
            Cancel
          </Button>
          <Button className='mt-2 me-auto' variant='primary' onClick={handleSubmit(handleSave)} disabled={isSaveDisabled} loading={isLoadingSave}>
            Save
          </Button>
        </div>
      </div>
      <ConfirmationModal />
    </div>
  )
}

export interface UpToDateProps {
  serviceId: string
}

export const UpToDate: React.FC<UpToDateProps> = ({ serviceId }) => {
  const integrationTypeDisplayName = getIntegrationCategoryDisplayNameByServiceId(serviceId)

  return (
    <div className='d-flex align-items-center justify-content-center contact-information-up-to-date label-large'>
      Your {integrationTypeDisplayName} is up to date!
    </div>
  )
}
